import { z } from 'zod';

// Simple UUID v4-ish validation (accepts any RFC4122 UUID)
export const isUuid = (value: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
};

// Username validation
export const usernameSchema = z.string()
  .trim()
  .min(2, 'Username must be at least 2 characters')
  .max(20, 'Username must be less than 20 characters')
  .regex(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores')
  .regex(/^[a-zA-Z]/, 'Username must start with a letter');

// Password policy rules (single source of truth)
export const PASSWORD_MIN_LENGTH = 10;

export const passwordSchema = z.string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(128, 'Password too long')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number');

export interface PasswordStrengthResult {
  score: number; // 0–5
  level: 'weak' | 'fair' | 'good' | 'strong';
  checks: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    special: boolean;
  };
}

export function checkPasswordStrength(password: string): PasswordStrengthResult {
  const checks = {
    length: password.length >= PASSWORD_MIN_LENGTH,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()\-_=+[\]{};':"\\|,.<>/?`~]/.test(password),
  };

  const score = Object.values(checks).filter(Boolean).length;

  let level: PasswordStrengthResult['level'];
  if (score <= 2) level = 'weak';
  else if (score === 3) level = 'fair';
  else if (score === 4) level = 'good';
  else level = 'strong';

  return { score, level, checks };
}

// Authentication validation
export const authSchema = z.object({
  email: z.string().email('Invalid email format').max(255, 'Email too long'),
  password: passwordSchema,
  name: z.string().trim().min(1, 'Name required').max(100, 'Name too long').optional()
});

// Marketplace bio validation (max 200 chars, trimmed, empty treated as null)
export const marketplaceBioSchema = z
  .string()
  .trim()
  .max(200, 'Marketplace bio must be 200 characters or less')
  .nullable()
  .optional();

// Listing validation
export const listingSchema = z.object({
  title: z.string().trim().min(3, 'Title must be at least 3 characters').max(100, 'Title too long'),
  price: z.string().refine((val) => {
    const num = parseFloat(val);
    return !isNaN(num) && num > 0 && num <= 10000000;
  }, 'Price must be between $0 and $10,000,000'),
  description: z.string().trim().min(10, 'Description must be at least 10 characters').max(2000, 'Description too long'),
  location: z.string().trim().min(1, 'Location required').max(200, 'Location too long'),
  category: z.enum(['goods', 'services']),
  subcategory: z.string().min(1, 'Subcategory required')
});

// Forum post validation
export const forumPostSchema = z.object({
  title: z.string().trim().max(200, 'Title too long').optional(),
  body: z.string().trim().min(10, 'Post must be at least 10 characters').max(5000, 'Post too long'),
  area: z.string().trim().min(2, 'Area required').max(100, 'Area too long').optional(),
  county: z.string().trim().min(1, 'County required').max(100, 'County too long'),
  city: z.string().trim().max(100, 'City too long').optional(),
  township: z.string().trim().max(100, 'Township too long').optional(),
  hashtags: z.array(z.string().trim().min(1).max(50)).max(10, 'Maximum 10 hashtags allowed').optional(),
  topic: z.enum(['Local News', 'Wanna Join?', 'Hood Q&A', 'Local Eats', 'Lost & Found', 'Local Info'])
});

// Waitlist validation
export const waitlistSchema = z.object({
  email: z.string().email('Invalid email format').max(255, 'Email too long')
});

// Search validation
export const searchSchema = z.object({
  query: z.string().trim().min(1, 'Search query required').max(200, 'Search query too long')
});
